import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardCheck, CheckCircle2, Clock, PauseCircle, TrendingUp, Building2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function Kpi({ icon: Icon, label, value, accent = "slate" }) {
  const cls = {
    slate: "bg-slate-50 text-slate-700", emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700", indigo: "bg-indigo-50 text-indigo-700",
    sky: "bg-sky-50 text-sky-700",
  }[accent];
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-3">
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${cls}`}><Icon size={20} /></div>
      <div>
        <div className="text-2xl font-bold text-slate-900 leading-none">{value}</div>
        <div className="text-xs text-slate-500 mt-1">{label}</div>
      </div>
    </div>
  );
}

export default function ApqpReports() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState([]);
  const [unitFilter, setUnitFilter] = useState("");
  const [active, setActive] = useState("summary");   // selected report in the left nav

  useEffect(() => {
    if (user?.role === "admin") api.get("/units").then((r) => setUnits(r.data || [])).catch(() => {});
  }, [user]);

  useEffect(() => {
    setLoading(true);
    api.get("/apqp/reports/summary", { params: unitFilter ? { unit_id: unitFilter } : {} })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [unitFilter]);

  const t = data?.totals || { total: 0, completed: 0, wip: 0, on_hold: 0, avg_progress: 0 };
  const cat = data?.by_category || { "2W": 0, "4W": 0, Unset: 0 };
  const catTotal = (cat["2W"] || 0) + (cat["4W"] || 0) + (cat.Unset || 0);
  const pct = (x) => (catTotal ? Math.round((x / catTotal) * 100) : 0);

  const SECTIONS = [
    { group: "Overview", items: [
      { id: "summary", label: "Summary" },
      { id: "segment", label: "2W vs 4W" },
    ] },
    { group: "Rollups", items: [
      { id: "by_plant", label: "By Plant" },
    ] },
    { group: "Projects", items: [
      { id: "projects", label: "All Projects" },
    ] },
  ];

  function renderSection() {
    switch (active) {
      case "segment":
        return (
          <section data-testid="apqp-reports-segment">
            <h2 className="text-base font-semibold text-slate-900 mb-3">2W vs 4W</h2>
            <div className="bg-white border border-slate-200 rounded-lg p-4 max-w-md">
              <div className="flex h-3 rounded-full overflow-hidden bg-slate-100 mb-3">
                <div className="bg-sky-500" style={{ width: `${pct(cat["2W"])}%` }} title={`2W ${cat["2W"]}`} />
                <div className="bg-indigo-500" style={{ width: `${pct(cat["4W"])}%` }} title={`4W ${cat["4W"]}`} />
                <div className="bg-slate-300" style={{ width: `${pct(cat.Unset)}%` }} title={`Unset ${cat.Unset}`} />
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-md bg-sky-50 py-2"><div className="text-lg font-bold text-sky-700">{cat["2W"]}</div><div className="text-[11px] text-slate-500">2W · {pct(cat["2W"])}%</div></div>
                <div className="rounded-md bg-indigo-50 py-2"><div className="text-lg font-bold text-indigo-700">{cat["4W"]}</div><div className="text-[11px] text-slate-500">4W · {pct(cat["4W"])}%</div></div>
                <div className="rounded-md bg-slate-50 py-2"><div className="text-lg font-bold text-slate-500">{cat.Unset}</div><div className="text-[11px] text-slate-400">Unset · {pct(cat.Unset)}%</div></div>
              </div>
            </div>
          </section>
        );
      case "by_plant":
        return (
          <section data-testid="apqp-reports-by-plant">
            <h2 className="text-base font-semibold text-slate-900 mb-3">APQP projects by plant</h2>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden max-w-xl">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider text-slate-400 bg-slate-50">
                  <tr><th className="text-left px-3 py-2">Plant</th><th className="text-right px-3 py-2">Projects</th><th className="text-right px-3 py-2">Completed</th><th className="text-right px-3 py-2">Avg progress</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(data.by_unit || []).length === 0 && <tr><td colSpan={4} className="px-3 py-4 text-center text-xs text-slate-400">No projects.</td></tr>}
                  {(data.by_unit || []).map((u) => (
                    <tr key={u.unit}>
                      <td className="px-3 py-2 text-slate-700 font-medium flex items-center gap-1.5"><Building2 size={13} className="text-slate-400" />{u.unit}</td>
                      <td className="px-3 py-2 text-right text-slate-600">{u.count}</td>
                      <td className="px-3 py-2 text-right text-emerald-700">{u.completed}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-600">{u.avg_progress}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      case "projects":
        return (
          <section data-testid="apqp-reports-projects">
            <h2 className="text-base font-semibold text-slate-900 mb-3">All APQP projects <span className="text-sm font-normal text-slate-400">({(data.projects || []).length})</span></h2>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-400">
                    <tr>
                      <th className="text-left px-3 py-2">APQP #</th>
                      <th className="text-left px-3 py-2">Part</th>
                      <th className="text-left px-3 py-2">Customer</th>
                      <th className="text-left px-3 py-2">Plant</th>
                      <th className="text-left px-3 py-2">Cat</th>
                      <th className="text-left px-3 py-2 w-40">Progress</th>
                      <th className="text-right px-3 py-2">On-time</th>
                      <th className="text-left px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(data.projects || []).length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-xs text-slate-400">No APQP projects.</td></tr>}
                    {(data.projects || []).map((p) => (
                      <tr key={p.apqp_id} className="hover:bg-slate-50">
                        <td className="px-3 py-2"><Link to={`/apqp/${p.apqp_id}`} className="font-mono text-xs text-orange-600 hover:underline">{p.apqp_number}</Link></td>
                        <td className="px-3 py-2"><div className="text-slate-800">{p.part_name}</div><div className="text-[10px] text-slate-400">{p.part_number}</div></td>
                        <td className="px-3 py-2 text-slate-600 text-xs">{p.customer_name}</td>
                        <td className="px-3 py-2 text-slate-500 text-xs">{p.unit}</td>
                        <td className="px-3 py-2 text-xs">{p.category || "—"}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                              <div className={`h-full ${p.progress_pct >= 100 ? "bg-emerald-500" : "bg-indigo-500"}`} style={{ width: `${Math.min(100, p.progress_pct)}%` }} />
                            </div>
                            <span className="text-[10px] font-mono text-slate-500 w-9 text-right">{p.progress_pct}%</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-slate-600">{p.on_time_pct}%</td>
                        <td className="px-3 py-2">
                          {p.on_hold ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">On hold</span>
                            : p.progress_pct >= 100 ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Complete</span>
                            : <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">WIP</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        );
      case "summary":
      default:
        return (
          <section data-testid="apqp-reports-summary">
            <h2 className="text-base font-semibold text-slate-900 mb-3">Summary</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Kpi icon={ClipboardCheck} label="Total projects" value={t.total} accent="indigo" />
              <Kpi icon={CheckCircle2} label="Completed" value={t.completed} accent="emerald" />
              <Kpi icon={Clock} label="In progress" value={t.wip} accent="amber" />
              <Kpi icon={PauseCircle} label="On hold" value={t.on_hold} accent="slate" />
              <Kpi icon={TrendingUp} label="Avg progress" value={`${t.avg_progress}%`} accent="sky" />
              <Kpi icon={Building2} label="Plants" value={(data?.by_unit || []).length} accent="sky" />
            </div>
          </section>
        );
    }
  }

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="apqp-reports-page">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardCheck size={22} className="text-orange-600" />
        <h1 className="text-2xl font-bold text-slate-900">APQP Reports</h1>
      </div>
      <p className="text-sm text-slate-500 mb-4">Pick a report on the left. APQP project stats across plants.</p>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 mb-4 flex items-center gap-3 flex-wrap">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Filters</span>
        {user?.role === "admin" ? (
          <select value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)}
            className="border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white" data-testid="apqp-reports-unit">
            <option value="">Unit: All</option>
            {units.map((u) => <option key={u.unit_id} value={u.unit_id}>{u.unit_code} — {u.name}</option>)}
          </select>
        ) : (
          <span className="text-xs text-slate-400">Your plant only</span>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : !data ? (
        <div className="text-sm text-slate-400">No data.</div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-4 items-start">
          {/* Left: report catalogue */}
          <nav className="w-full lg:w-56 shrink-0 bg-white border border-slate-200 rounded-lg p-2" data-testid="apqp-reports-nav">
            {SECTIONS.map((g) => (
              <div key={g.group} className="mb-2">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 px-2 mb-1">{g.group}</div>
                {g.items.map((it) => (
                  <button key={it.id} onClick={() => setActive(it.id)}
                    className={`w-full text-left px-3 py-1.5 rounded-md text-sm mb-0.5 border-l-2 transition ${active === it.id ? "bg-orange-50 text-orange-700 border-orange-500 font-medium" : "border-transparent text-slate-600 hover:bg-slate-50"}`}
                    data-testid={`apqp-reports-nav-${it.id}`}>
                    {it.label}
                  </button>
                ))}
              </div>
            ))}
          </nav>
          {/* Right: selected report */}
          <div className="flex-1 min-w-0 w-full">
            {renderSection()}
          </div>
        </div>
      )}
    </div>
  );
}
